/* eslint-disable no-unused-vars */

// ===== Packages =====

import {
    Node,
    Editor,
    Path,
    NodeEntry,
    Transforms,
    Range,
}                           from 'slate';
import { v4 as uuidv4 }     from 'uuid';

// ===== Elements =====

import _BlockQuoteElement   from '../elements/blockQuoteElement';
import LineElement          from '../elements/lineElement';

// ===== Interfaces =====

import {
    EditorNode,
    BlockQuoteNode,
}                           from '../interfaces';

export const BlockQuoteElement = _BlockQuoteElement;

/**
 * Block quotes are line-like link targets, so preserve an existing paragraphId
 * whenever we toggle into or out of quote mode.
 */
const getExistingParagraphId = (node: EditorNode): string => {
    const { paragraphId } = node;
    return typeof paragraphId === 'string' ? paragraphId.trim() : '';
};

const setAsBlockQuoteElement = (editor: Editor): void => {
    Editor.withoutNormalizing(editor, () => {
        Array.from(Editor.nodes(editor, { match: BlockQuoteElement.implementsLineInterface })).forEach(([node, path]) => {
            const existingParagraphId = getExistingParagraphId(node as EditorNode);
            Transforms.setNodes(
                editor,
                BlockQuoteElement.newBlockQuoteElement({
                    paragraphId: existingParagraphId || uuidv4(),
                }),
                { at: path },
            );
        });
    });
};

const getSelectionBlockQuotes = (editor: Editor): NodeEntry<Node>[] => Array.from(
    Editor.nodes(editor, { match: BlockQuoteElement.isBlockQuoteElement }),
);

const hasSelectedBlockQuote = (editor: Editor): boolean => {
    const [match] = Array.from(Editor.nodes(editor, { match: BlockQuoteElement.isBlockQuoteElement }));
    return !!match;
};

const toggleBlockQuote = (editor: Editor): void => {
    if (hasSelectedBlockQuote(editor)) {
        Editor.withoutNormalizing(editor, () => {
            Array.from(Editor.nodes(editor, { match: BlockQuoteElement.isBlockQuoteElement })).forEach(([node, path]) => {
                const existingParagraphId = getExistingParagraphId(node as EditorNode);
                Transforms.setNodes(editor, {
                    ...LineElement.newLineElement(),
                    ...(existingParagraphId ? { paragraphId: existingParagraphId } : {}),
                }, { at: path });
            });
        });
    } else {
        setAsBlockQuoteElement(editor);
    }
};

export const BlockQuoteHelpers = {
    getSelectionBlockQuotes,
    setAsBlockQuoteElement,
    hasSelectedBlockQuote,
    toggleBlockQuote,
};

export const withBlockQuotes = (editor: Editor, _readOnly?: boolean): Editor => {
    const {
        normalizeNode,
        isInline,
        isVoid,
        insertBreak,
    } = editor;

    // eslint-disable-next-line no-param-reassign
    editor.normalizeNode = (entry) => {
        const [, path] = entry;
        // NOTE: manually grab the node because entry will be changed
        //  to path in the near future per https://github.com/ianstormtaylor/slate/issues/3275
        const node = Node.get(editor, path) as EditorNode;

        if (!BlockQuoteElement.isBlockQuoteElement(node)) { return normalizeNode(entry); }

        const blockQuote = node;

        if (Editor.hasBlocks(editor, blockQuote)) { return Transforms.unwrapNodes(editor, { at: path }); }

        const blockQuoteDeclaresIsLineProperty = node.isLine === true;
        if (!blockQuoteDeclaresIsLineProperty) { return Transforms.setNodes<BlockQuoteNode>(editor, { isLine: true }, { at: path }); }

        const blockQuoteDeclaresIsListableProperty = node.isListable === true;
        if (!blockQuoteDeclaresIsListableProperty) { return Transforms.setNodes<BlockQuoteNode>(editor, { isListable: true }, { at: path }); }

        const paragraphId = typeof blockQuote.paragraphId === 'string'
            ? blockQuote.paragraphId.trim()
            : '';
        if (!paragraphId) {
            return Transforms.setNodes<BlockQuoteNode>(editor, { paragraphId: uuidv4() }, { at: path });
        }

        // Split/paste flows can duplicate node props, so keep every quote target unique.
        const firstMatchingBlockQuotePath = Array.from(Editor.nodes(editor, {
            at: [],
            match: (candidate) => (
                BlockQuoteElement.isBlockQuoteElement(candidate)
                && 'paragraphId' in candidate
                && candidate.paragraphId === paragraphId
            ),
        }))
            .map(([, matchingPath]) => matchingPath)[0];
        if (
            firstMatchingBlockQuotePath
            && !Path.equals(firstMatchingBlockQuotePath, path)
        ) {
            return Transforms.setNodes<BlockQuoteNode>(editor, { paragraphId: uuidv4() }, { at: path });
        }

        return normalizeNode(entry);
    };

    // eslint-disable-next-line no-param-reassign
    editor.isInline = (element) => (BlockQuoteElement.isBlockQuoteElement(element) ? false : isInline(element));

    // eslint-disable-next-line no-param-reassign
    editor.isVoid = (node) => (BlockQuoteElement.isBlockQuoteElement(node) ? false : isVoid(node));

    // eslint-disable-next-line no-param-reassign
    editor.insertBreak = () => {
        const [blockQuoteEntry] = Array.from(Editor.nodes(editor, { match: BlockQuoteElement.isBlockQuoteElement }));
        const { selection }: { selection: Range | null } = editor;
        if (blockQuoteEntry && selection) {
            const [start, end] = Range.edges(selection);
            const [startBlockQuoteEntry] = Array.from(Editor.nodes(editor, { at: start.path, match: BlockQuoteElement.isBlockQuoteElement }));
            const [endBlockQuoteEntry] = Array.from(Editor.nodes(editor, { at: end.path, match: BlockQuoteElement.isBlockQuoteElement }));
            if (startBlockQuoteEntry && endBlockQuoteEntry) {
                return Transforms.insertText(editor, '\n');
            }
            if (startBlockQuoteEntry) {
                const [[endNode]] = Array.from(
                    Editor.nodes(editor, { at: end.path, match: (n) => Editor.isBlock(editor, n), mode: 'lowest' }),
                );
                insertBreak();
                return Editor.withoutNormalizing(editor, () => {
                    Transforms.setNodes(editor, endNode as EditorNode);
                    Transforms.unsetNodes(editor, ['headerSize', 'isLine', 'isListable']);
                });
            }
            if (endBlockQuoteEntry) {
                insertBreak();
                return Transforms.setNodes(editor, BlockQuoteElement.newBlockQuoteElement());
            }
        }

        return insertBreak();
    };

    return editor;
};
